// The balance of the workspace for the sidebar meter: loaded when the workspace or the place in view changes, again
// as soon as a generation was started or finished its start (the app announces it with `announceCreditsChange`),
// and every 60 s while the tab is visible.
import { useEffect, useState } from "react";
import { browserHost, keepIfSame, startQuietRefresh } from "./quietRefresh.ts";
import type { Balance, BillingSource } from "./sources.ts";

const EVENT = "filmsquare:credits";

/** Tells the app that credits probably moved (a generation was started, a request was refused for credits). */
export function announceCreditsChange(target: EventTarget = window): void {
  target.dispatchEvent(new Event(EVENT));
}

/** True for the requests that start paid work: POST …/generations, …/generations/{id}/(promote|retry), node runs. */
export function startsPaidWork(method: string, url: string): boolean {
  if (method.toUpperCase() !== "POST") return false;
  const path = url.split("?")[0] ?? "";
  return /\/generations(\/[^/]+\/(promote|retry))?$/.test(path) || /\/nodes\/[^/]+\/run$/.test(path);
}

/** Follow-ups after a start: the cost is booked when the work ends, so look again a little later. */
export const FOLLOW_UPS_MS = [20_000, 60_000];

export function useBalance(billing: BillingSource, ws: string, placeKey: string): Balance | null {
  // remembered with its workspace: a balance never shows under another workspace's name
  const [state, setState] = useState<{ ws: string; balance: Balance } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: placeKey reloads when the route changes
  useEffect(() => {
    let live = true;
    const load = () =>
      billing.balance(ws).then(
        (b) =>
          live &&
          b &&
          setState((prev) => ({ ws, balance: prev?.ws === ws ? keepIfSame(prev.balance, b) : b })),
        () => undefined, // a quiet refresh keeps what is on screen
      );
    void load();
    const timers: ReturnType<typeof setTimeout>[] = [];
    const onChange = () => {
      void load();
      for (const ms of FOLLOW_UPS_MS) timers.push(setTimeout(() => void load(), ms));
    };
    window.addEventListener(EVENT, onChange);
    const stop = startQuietRefresh({ run: load, everyMs: 60_000, host: browserHost() });
    return () => {
      live = false;
      window.removeEventListener(EVENT, onChange);
      for (const t of timers) clearTimeout(t);
      stop();
    };
  }, [billing, ws, placeKey]);
  return state?.ws === ws ? state.balance : null;
}

/** Runs `reload` whenever credits probably moved (the Credits screen refreshes its ledger this way). */
export function useCreditsChanged(reload: () => void): void {
  useEffect(() => {
    window.addEventListener(EVENT, reload);
    return () => window.removeEventListener(EVENT, reload);
  }, [reload]);
}
